import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  Settings as SettingsIcon,
  Clock,
  Bell,
  Loader2,
  Check,
  CalendarClock,
  Calendar,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  Plug,
  MapPin,
  Send,
  CheckCircle2,
  XCircle,
  BellRing,
  Tag,
  Pencil,
  X,
  ChevronRight,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import SyncProgressBar from '../components/SyncProgressBar';
import { useLocationContext } from '../context/LocationContext';

const DAY_OPTIONS = [
  'thursday',
  'friday',
  'saturday',
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
];

const APPT_FROM_OPTIONS = [
  { value: 'next_monday', label: 'Next Monday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const SETTINGS_SECTIONS = [
  { id: 'reminders', label: 'Auto Reminders', icon: Bell },
  { id: 'schedule', label: 'Reminder Schedule', icon: Calendar },
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'scheduled-reminders', label: 'Appointment Alerts', icon: BellRing },
  { id: 'filters', label: 'Filters', icon: CalendarClock },
  { id: 'job-categories', label: 'Job Categories', icon: Tag },
  { id: 'database', label: 'Database Reset', icon: Trash2 },
];

export default function Settings() {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);
  const [editSchedule, setEditSchedule] = useState(null);
  const { selectedLocationId, currentLocation } = useLocationContext();
  const [testPhone, setTestPhone] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [editSR, setEditSR] = useState(null);
  const [activeSection, setActiveSection] = useState('reminders');
  // Job categories state
  const [newCatName, setNewCatName] = useState('');
  const [newCatUid, setNewCatUid] = useState('');
  const [editingCatUid, setEditingCatUid] = useState(null);
  const [editCatName, setEditCatName] = useState('');
  const [expandedCatUid, setExpandedCatUid] = useState(null);

  // Lazily fetch the status workflow (name + UID) for whichever category is expanded.
  const { data: catStatusData, isLoading: catStatusesLoading, isError: catStatusesError } = useQuery({
    queryKey: ['category-statuses', expandedCatUid],
    queryFn: async () => {
      const { data } = await axios.get(`/api/settings/zuper-categories/${expandedCatUid}/statuses`);
      return data;
    },
    enabled: !!expandedCatUid,
    staleTime: 10 * 60 * 1000,
  });

  // Fetch settings (location-scoped)
  const { data: settings, isLoading } = useQuery({
    queryKey: ['app-settings', selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get('/api/settings');
      return data;
    },
  });

  const autoReminderEnabled = settings?.autoReminderEnabled ?? true;
  const timeSlots = settings?.timeSlots || [];
  const schedule = settings?.reminderSchedule || [];
  const weekCycle = settings?.schedulerConfig?.weekCycle || 'Thursday to Wednesday';
  const mechanics = settings?.mechanics || [];

  // Fetch Zuper categories
  const { data: categoriesData, refetch: refetchCategories } = useQuery({
    queryKey: ['zuper-categories', selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get('/api/settings/zuper-categories');
      return data;
    },
  });
  const allCategories = categoriesData?.categories || [];

  // Job category mutations
  const addCatMutation = useMutation({
    mutationFn: async ({ category_uid, category_name }) => {
      const { data } = await axios.post('/api/settings/zuper-categories', { category_uid, category_name });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['zuper-categories']);
      setNewCatName('');
      setNewCatUid('');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const editCatMutation = useMutation({
    mutationFn: async ({ category_uid, category_name }) => {
      const { data } = await axios.put(`/api/settings/zuper-categories/${category_uid}`, { category_name });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['zuper-categories']);
      setEditingCatUid(null);
      setEditCatName('');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const deleteCatMutation = useMutation({
    mutationFn: async (uid) => {
      const { data } = await axios.delete(`/api/settings/zuper-categories/${uid}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['zuper-categories']);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const handleAddCat = () => {
    if (!newCatName.trim() || !newCatUid.trim()) return;
    addCatMutation.mutate({ category_uid: newCatUid.trim(), category_name: newCatName.trim() });
  };

  const handleSaveEditCat = () => {
    if (!editCatName.trim()) return;
    editCatMutation.mutate({ category_uid: editingCatUid, category_name: editCatName.trim() });
  };

  // Integration settings state
  const [editIntegrations, setEditIntegrations] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});

  // Sync editSchedule when server data loads/changes
  useEffect(() => {
    if (settings?.reminderSchedule && editSchedule === null) {
      setEditSchedule(null); // keep null until user edits
    }
  }, [settings?.reminderSchedule, editSchedule]);

  // Sync Zuper data mutation
  const syncZuperMutation = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post('/api/settings/sync-zuper');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-settings'] });
      queryClient.invalidateQueries({ queryKey: ['zuper-categories'] });
    },
  });

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: async (updates) => {
      const { data } = await axios.post('/api/settings', updates);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const handleToggle = () => {
    updateMutation.mutate({ autoReminderEnabled: !autoReminderEnabled });
  };

  // Schedule editing helpers
  const isEditing = editSchedule !== null;
  const displaySchedule = isEditing ? editSchedule : schedule;

  const startEditing = () => {
    setEditSchedule([...schedule.map((e) => ({ ...e }))]);
  };

  const cancelEditing = () => {
    setEditSchedule(null);
  };

  const updateEntry = (index, field, value) => {
    setEditSchedule((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)),
    );
  };

  const removeEntry = (index) => {
    setEditSchedule((prev) => prev.filter((_, i) => i !== index));
  };

  const addEntry = () => {
    setEditSchedule((prev) => [
      ...prev,
      { day: 'monday', time: '10:30', appointmentsFrom: 'tuesday' },
    ]);
  };

  const saveSchedule = () => {
    updateMutation.mutate({ reminderSchedule: editSchedule });
    setEditSchedule(null);
  };

  // Format time for display (24h -> 12h)
  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
  };

  const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

  // Integration editing helpers
  const isEditingIntegrations = editIntegrations !== null;

  const startEditingIntegrations = () => {
    setEditIntegrations({
      zuper: {
        apiBaseUrl: settings?.zuper?.apiBaseUrl || '',
        apiToken: '',
        categoryStatusUids: settings?.zuper?.categoryStatusUids || {},
      },
      whatsapp: {
        apiUrl: settings?.whatsapp?.apiUrl || '',
        apiKey: '',
        wabaNumber: settings?.whatsapp?.wabaNumber || '',
        templateName: settings?.whatsapp?.templateName || '',
        confirmationTemplateName: settings?.whatsapp?.confirmationTemplateName || '',
      },
      booking: {
        baseUrl: settings?.booking?.baseUrl || '',
        secretKey: '',
      },
    });
    setShowSecrets({});
  };

  const cancelEditingIntegrations = () => {
    setEditIntegrations(null);
    setShowSecrets({});
  };

  const updateIntegration = (section, field, value) => {
    setEditIntegrations((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const updateCategoryUid = (categoryName, type, value) => {
    setEditIntegrations((prev) => ({
      ...prev,
      zuper: {
        ...prev.zuper,
        categoryStatusUids: {
          ...prev.zuper.categoryStatusUids,
          [categoryName]: {
            ...prev.zuper.categoryStatusUids[categoryName],
            [type]: value,
          },
        },
      },
    }));
  };

  const saveIntegrations = () => {
    const payload = {};
    const z = editIntegrations.zuper;
    const w = editIntegrations.whatsapp;
    const b = editIntegrations.booking;

    // Only send fields that have actual values (empty = don't change secret)
    payload.zuper = {};
    if (z.apiBaseUrl) payload.zuper.apiBaseUrl = z.apiBaseUrl;
    if (z.apiToken) payload.zuper.apiToken = z.apiToken;
    payload.zuper.categoryStatusUids = z.categoryStatusUids;

    payload.whatsapp = {};
    if (w.apiUrl) payload.whatsapp.apiUrl = w.apiUrl;
    if (w.apiKey) payload.whatsapp.apiKey = w.apiKey;
    payload.whatsapp.wabaNumber = w.wabaNumber; // allow clearing
    if (w.templateName) payload.whatsapp.templateName = w.templateName;
    if (w.confirmationTemplateName) payload.whatsapp.confirmationTemplateName = w.confirmationTemplateName;

    payload.booking = {};
    if (b.baseUrl) payload.booking.baseUrl = b.baseUrl;
    if (b.secretKey) payload.booking.secretKey = b.secretKey;

    updateMutation.mutate(payload);
    setEditIntegrations(null);
    setShowSecrets({});
  };

  const toggleSecret = (key) => {
    setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const formatAppointmentsFrom = (s) => {
    if (!s) return '';
    if (s === 'next_monday') return 'Next Monday';
    return capitalize(s);
  };

  return (
    <Layout>
      {/* Header */}
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm relative">
        <SyncProgressBar isSyncing={syncZuperMutation.isPending} />
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <SettingsIcon className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Settings</h1>
            <p className="text-xs text-brand-neutral hidden md:block">Auto reminders, integrations, and booking configuration</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {currentLocation && (
            <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
              <MapPin className="w-3 h-3" />
              {currentLocation.name}
            </span>
          )}
          {saved && (
            <div className="flex items-center gap-1.5 text-green-600 text-sm">
              <Check className="w-4 h-4" />
              Saved
            </div>
          )}
          <button
            onClick={() => syncZuperMutation.mutate()}
            disabled={syncZuperMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-brand-dark bg-white border border-brand-light rounded-xl hover:bg-brand-light transition-colors shadow-sm disabled:opacity-50"
            title="Sync settings from Zuper"
          >
            <RefreshCw className={`w-4 h-4 ${syncZuperMutation.isPending ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncZuperMutation.isPending ? 'Syncing...' : 'Sync Zuper'}</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-hidden flex bg-[#f5f8f6]">
        {/* Settings Sidebar */}
        <nav className="w-52 flex-shrink-0 bg-[#f5f8f6] overflow-y-auto pt-4 px-3 space-y-1 hidden md:block">
          {SETTINGS_SECTIONS.map(sec => {
            const Icon = sec.icon;
            const isActive = activeSection === sec.id;
            return (
              <button key={sec.id} onClick={() => setActiveSection(sec.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all ${
                  isActive ? 'bg-brand-primary/10 text-brand-primary' : 'text-brand-dark/60 hover:bg-brand-light/60 hover:text-brand-primary'
                }`}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                {sec.label}
              </button>
            );
          })}
        </nav>
        {/* Mobile section selector */}
        <div className="md:hidden p-3 bg-white border-b border-brand-light/60 flex gap-1 overflow-x-auto flex-shrink-0">
          {SETTINGS_SECTIONS.map(sec => (
            <button key={sec.id} onClick={() => setActiveSection(sec.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${
                activeSection === sec.id ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-600'
              }`}>{sec.label}</button>
          ))}
        </div>
        {/* Settings Content */}
        <div className="flex-1 overflow-y-auto p-3 md:p-5">
          <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
            </div>
          ) : (
            <>
              {activeSection === 'reminders' && (<>
              {/* Auto Reminders Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white">
                  <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                    <Bell className="w-4 h-4 text-brand-primary" />
                    Auto Reminders
                  </h3>
                  <p className="text-xs text-brand-neutral mt-0.5">
                    Control automatic WhatsApp reminder sending
                    {currentLocation && <span className="ml-1 font-medium text-brand-primary">({currentLocation.name})</span>}
                  </p>
                </div>
                <div className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-brand-dark">
                        Auto-send reminders
                      </p>
                      <p className="text-xs text-brand-neutral mt-0.5">
                        {autoReminderEnabled
                          ? 'Reminders are sent automatically on schedule'
                          : 'Automatic sending is paused'}
                      </p>
                    </div>
                    <button
                      onClick={handleToggle}
                      disabled={updateMutation.isPending}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none ${autoReminderEnabled ? 'bg-brand-primary' : 'bg-gray-300'
                        } ${updateMutation.isPending ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${autoReminderEnabled
                          ? 'translate-x-6'
                          : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              </>)}
              {activeSection === 'schedule' && (<>
              {/* Reminder Schedule Card (Editable) */}
              <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-brand-primary" />
                        Reminder Schedule
                      </h3>
                      <p className="text-xs text-brand-neutral mt-0.5">
                        Week cycle:{' '}
                        <span className="font-medium text-brand-dark">
                          {weekCycle}
                        </span>
                        . Changes take effect immediately.
                      </p>
                    </div>
                    {!isEditing && (
                      <button
                        onClick={startEditing}
                        className="text-xs font-medium text-brand-primary hover:text-brand-secondary px-3 py-1.5 rounded-lg hover:bg-brand-light transition-colors"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-4 md:p-6">
                  {displaySchedule.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-brand-neutral">
                        No schedule configured
                      </p>
                      {!isEditing && (
                        <button
                          onClick={startEditing}
                          className="mt-2 text-xs text-brand-primary hover:underline"
                        >
                          Add schedule entries
                        </button>
                      )}
                    </div>
                  ) : isEditing ? (
                    /* Edit Mode */
                    <div className="space-y-2">
                      {editSchedule.map((entry, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60 hover:border-brand-light transition-all"
                        >
                          {/* Day */}
                          <select
                            value={entry.day}
                            onChange={(e) =>
                              updateEntry(index, 'day', e.target.value)
                            }
                            className="text-xs font-medium text-brand-dark bg-white border border-brand-light/80 rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                          >
                            {DAY_OPTIONS.map((d) => (
                              <option key={d} value={d}>
                                {capitalize(d)}
                              </option>
                            ))}
                          </select>

                          {/* Time */}
                          <input
                            type="time"
                            value={entry.time}
                            onChange={(e) =>
                              updateEntry(index, 'time', e.target.value)
                            }
                            className="text-xs text-brand-dark bg-white border border-brand-light/80 rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary w-32 transition-all"
                          />

                          {/* Appointments From */}
                          <span className="text-[10px] text-brand-neutral whitespace-nowrap">
                            from
                          </span>
                          <select
                            value={entry.appointmentsFrom}
                            onChange={(e) =>
                              updateEntry(
                                index,
                                'appointmentsFrom',
                                e.target.value,
                              )
                            }
                            className="text-xs text-brand-dark bg-white border border-brand-light/80 rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary flex-1 transition-all"
                          >
                            {APPT_FROM_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>

                          {/* Delete */}
                          <button
                            onClick={() => removeEntry(index)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}

                      {/* Add + Save/Cancel buttons */}
                      <div className="flex items-center justify-between pt-2">
                        <button
                          onClick={addEntry}
                          className="flex items-center gap-1.5 text-xs font-medium text-brand-primary hover:text-brand-secondary transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add Entry
                        </button>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={cancelEditing}
                            className="text-xs text-brand-neutral hover:text-brand-dark px-3 py-1.5 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={saveSchedule}
                            disabled={updateMutation.isPending}
                            className="flex items-center gap-1.5 text-xs font-medium text-white bg-brand-primary hover:bg-brand-secondary px-4 py-2 rounded-xl transition-colors disabled:opacity-50 shadow-sm"
                          >
                            <Save className="w-3 h-3" />
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="space-y-1.5">
                      {displaySchedule.map((entry, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-brand-dark w-24">
                              {capitalize(entry.day)}
                            </span>
                            <span className="text-xs text-brand-neutral">
                              {formatTime(entry.time)}
                            </span>
                          </div>
                          <span className="text-xs text-brand-neutral">
                            Appointments from{' '}
                            <span className="font-medium text-brand-dark">
                              {formatAppointmentsFrom(entry.appointmentsFrom)}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Booking Time Slots Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white">
                  <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                    <CalendarClock className="w-4 h-4 text-brand-primary" />
                    Booking Time Slots
                  </h3>
                  <p className="text-xs text-brand-neutral mt-0.5">
                    AMC service windows shown to customers
                  </p>
                </div>
                <div className="p-4 md:p-6">
                  {timeSlots.length === 0 ? (
                    <p className="text-sm text-brand-neutral text-center py-4">
                      No time slots configured
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {timeSlots.map((slot, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between px-4 py-3 bg-brand-light/20 rounded-xl border border-brand-light/60 hover:border-brand-light transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <Clock className="w-4 h-4 text-brand-neutral" />
                            <div>
                              <span className="text-sm font-medium text-brand-dark">
                                {slot.label ||
                                  `${formatTime(slot.start)} – ${formatTime(slot.end)}`}
                              </span>
                              {slot.description && (
                                <p className="text-xs text-brand-neutral mt-0.5">
                                  {slot.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-brand-neutral whitespace-nowrap ml-2">
                            {formatTime(slot.start)} – {formatTime(slot.end)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              </>)}
              {activeSection === 'scheduled-reminders' && (<>
              {/* Booked Reminders Card */}
              {(() => {
                const sr = settings?.scheduledReminders || {};
                const activeCats = editSR ? editSR.categories : (sr.categories || null);
                const isAll = activeCats === null || activeCats.length === 0;

                const startEditSR = () => setEditSR({
                  dayBeforeTime: sr.dayBeforeTime || '10:00',
                  sameDayTime: sr.sameDayTime || '06:00',
                  dayBeforeTemplate: sr.dayBeforeTemplate || 'amc_day_before_reminder',
                  sameDayTemplate: sr.sameDayTemplate || 'amc_same_day_reminder',
                  categories: sr.categories ? [...sr.categories] : null,
                });
                const cancelEditSR = () => setEditSR(null);
                const saveSR = () => {
                  updateMutation.mutate({ scheduledReminders: editSR });
                  setEditSR(null);
                };
                const toggleCat = (name) => {
                  setEditSR(prev => {
                    const current = prev.categories || [];
                    const next = current.includes(name)
                      ? current.filter(c => c !== name)
                      : [...current, name];
                    return { ...prev, categories: next.length === 0 ? null : next };
                  });
                };

                return (
                  <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                    <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-linear-to-r from-brand-light/40 to-white">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                          <BellRing className="w-4 h-4 text-brand-primary" />
                          Booked Reminders
                        </h3>
                        {editSR ? (
                          <div className="flex items-center gap-2">
                            <button onClick={cancelEditSR} className="text-xs text-brand-neutral hover:text-brand-dark px-3 py-1.5 rounded-lg transition-colors">Cancel</button>
                            <button onClick={saveSR} disabled={updateMutation.isPending} className="flex items-center gap-1.5 text-xs font-medium text-white bg-brand-primary hover:bg-brand-secondary px-4 py-2 rounded-xl transition-colors disabled:opacity-50 shadow-sm">
                              <Save className="w-3 h-3" /> Save
                            </button>
                          </div>
                        ) : (
                          <button onClick={startEditSR} className="text-xs text-brand-primary hover:text-brand-secondary font-medium transition-colors">Edit</button>
                        )}
                      </div>
                    </div>
                    <div className="p-4 md:p-6 space-y-4">
                      {/* Times */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-brand-neutral mb-1 block">Day-Before Send Time</label>
                          {editSR ? (
                            <input type="time" value={editSR.dayBeforeTime} onChange={e => setEditSR(p => ({ ...p, dayBeforeTime: e.target.value }))}
                              className="w-full text-sm text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all" />
                          ) : (
                            <div className="px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60 text-sm font-medium text-brand-dark">{sr.dayBeforeTime || '10:00'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-xs text-brand-neutral mb-1 block">Same-Day Send Time</label>
                          {editSR ? (
                            <input type="time" value={editSR.sameDayTime} onChange={e => setEditSR(p => ({ ...p, sameDayTime: e.target.value }))}
                              className="w-full text-sm text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all" />
                          ) : (
                            <div className="px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60 text-sm font-medium text-brand-dark">{sr.sameDayTime || '06:00'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-xs text-brand-neutral mb-1 block">Day-Before Template</label>
                          {editSR ? (
                            <input type="text" value={editSR.dayBeforeTemplate} onChange={e => setEditSR(p => ({ ...p, dayBeforeTemplate: e.target.value }))}
                              className="w-full text-xs font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all" />
                          ) : (
                            <div className="px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60 text-xs font-mono text-brand-dark">{sr.dayBeforeTemplate || 'amc_day_before_reminder'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-xs text-brand-neutral mb-1 block">Same-Day Template</label>
                          {editSR ? (
                            <input type="text" value={editSR.sameDayTemplate} onChange={e => setEditSR(p => ({ ...p, sameDayTemplate: e.target.value }))}
                              className="w-full text-xs font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all" />
                          ) : (
                            <div className="px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60 text-xs font-mono text-brand-dark">{sr.sameDayTemplate || 'amc_same_day_reminder'}</div>
                          )}
                        </div>
                      </div>

                      {/* Category filter */}
                      <div>
                        <label className="text-xs font-bold text-brand-dark uppercase tracking-wider mb-2 block">Job Categories to Include</label>
                        {editSR ? (
                          <div className="space-y-1.5">
                            <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-brand-light/60 bg-brand-light/10 cursor-pointer hover:bg-brand-light/30 transition-colors">
                              <input type="checkbox" checked={editSR.categories === null || editSR.categories.length === 0}
                                onChange={() => setEditSR(p => ({ ...p, categories: null }))}
                                className="rounded accent-brand-primary" />
                              <span className="text-xs font-medium text-brand-dark">All categories</span>
                            </label>
                            {allCategories.map(cat => (
                              <label key={cat.category_uid} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-brand-light/60 bg-brand-light/10 cursor-pointer hover:bg-brand-light/30 transition-colors">
                                <input type="checkbox"
                                  checked={(editSR.categories || []).includes(cat.category_name)}
                                  onChange={() => toggleCat(cat.category_name)}
                                  className="rounded accent-brand-primary" />
                                <span className="text-xs text-brand-dark">{cat.category_name}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                            <span className="text-xs text-brand-dark">
                              {isAll ? 'All categories' : (activeCats || []).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              </>)}
              {activeSection === 'integrations' && (<>
              {/* Integrations Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                        <Plug className="w-4 h-4 text-brand-primary" />
                        Integrations
                      </h3>
                      <p className="text-xs text-brand-neutral mt-0.5">
                        API connections and configuration
                      </p>
                    </div>
                    {!isEditingIntegrations && (
                      <button
                        onClick={startEditingIntegrations}
                        className="text-xs font-medium text-brand-primary hover:text-brand-secondary px-3 py-1.5 rounded-lg hover:bg-brand-light transition-colors"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-4 md:p-6 space-y-5">
                  {isEditingIntegrations ? (
                    <>
                      {/* Zuper API */}
                      <div className="space-y-3">
                        <h4 className="text-[11px] font-bold text-brand-dark uppercase tracking-wider flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                          Zuper API
                        </h4>
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs text-brand-neutral mb-1 block">API Base URL</label>
                            <input
                              type="text"
                              value={editIntegrations.zuper.apiBaseUrl}
                              onChange={(e) => updateIntegration('zuper', 'apiBaseUrl', e.target.value)}
                              className="w-full text-xs font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                              placeholder="https://ap-south-1.zuperpro.com/api"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-brand-neutral mb-1 block">API Token</label>
                            <div className="relative">
                              <input
                                type={showSecrets.zuperToken ? 'text' : 'password'}
                                value={editIntegrations.zuper.apiToken}
                                onChange={(e) => updateIntegration('zuper', 'apiToken', e.target.value)}
                                className="w-full text-xs font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 pr-9 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                                placeholder={settings?.zuper?.apiToken || 'Enter token'}
                              />
                              <button type="button" onClick={() => toggleSecret('zuperToken')} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-neutral hover:text-brand-dark">
                                {showSecrets.zuperToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* WhatsApp / DoubleTick */}
                      <div className="space-y-3">
                        <h4 className="text-[11px] font-bold text-brand-dark uppercase tracking-wider flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                          WhatsApp / DoubleTick
                        </h4>
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs text-brand-neutral mb-1 block">API URL</label>
                            <input
                              type="text"
                              value={editIntegrations.whatsapp.apiUrl}
                              onChange={(e) => updateIntegration('whatsapp', 'apiUrl', e.target.value)}
                              className="w-full text-xs font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                              placeholder="https://public.doubletick.io"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-brand-neutral mb-1 block">API Key</label>
                            <div className="relative">
                              <input
                                type={showSecrets.whatsappKey ? 'text' : 'password'}
                                value={editIntegrations.whatsapp.apiKey}
                                onChange={(e) => updateIntegration('whatsapp', 'apiKey', e.target.value)}
                                className="w-full text-xs font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 pr-9 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                                placeholder={settings?.whatsapp?.apiKey || 'Enter key'}
                              />
                              <button type="button" onClick={() => toggleSecret('whatsappKey')} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-neutral hover:text-brand-dark">
                                {showSecrets.whatsappKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-brand-neutral mb-1 block">WABA Number <span className="text-brand-neutral/60">(sender, e.g. 971542249901)</span></label>
                            <input
                              type="text"
                              value={editIntegrations.whatsapp.wabaNumber}
                              onChange={(e) => updateIntegration('whatsapp', 'wabaNumber', e.target.value)}
                              className="w-full text-xs font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                              placeholder="Leave blank to use API key default"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-brand-neutral mb-1 block">Reminder Template</label>
                              <input
                                type="text"
                                value={editIntegrations.whatsapp.templateName}
                                onChange={(e) => updateIntegration('whatsapp', 'templateName', e.target.value)}
                                className="w-full text-xs font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-brand-neutral mb-1 block">Confirmation Template</label>
                              <input
                                type="text"
                                value={editIntegrations.whatsapp.confirmationTemplateName}
                                onChange={(e) => updateIntegration('whatsapp', 'confirmationTemplateName', e.target.value)}
                                className="w-full text-xs font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                              />
                            </div>
                            {settings?.bookingBaseUrl && (
                              <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                                <div className="flex items-center justify-between mb-1">
                                  <label className="text-xs text-brand-neutral">Webhook URL <span className="text-amber-600">(configure in DoubleTick dashboard)</span></label>
                                  <button
                                    type="button"
                                    onClick={() => navigator.clipboard.writeText(`${settings.bookingBaseUrl}/api/webhooks/whatsapp`)}
                                    className="text-[10px] text-brand-primary hover:underline"
                                  >
                                    Copy
                                  </button>
                                </div>
                                <span className="text-xs font-mono text-brand-dark/70 break-all">{settings.bookingBaseUrl}/api/webhooks/whatsapp</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Booking */}
                      <div className="space-y-3">
                        <h4 className="text-[11px] font-bold text-brand-dark uppercase tracking-wider flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                          Booking
                        </h4>
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs text-brand-neutral mb-1 block">Base URL</label>
                            <input
                              type="text"
                              value={editIntegrations.booking.baseUrl}
                              onChange={(e) => updateIntegration('booking', 'baseUrl', e.target.value)}
                              className="w-full text-xs font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                              placeholder="https://amc.golfbuggyguy.com"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-brand-neutral mb-1 block">Secret Key</label>
                            <div className="relative">
                              <input
                                type={showSecrets.bookingSecret ? 'text' : 'password'}
                                value={editIntegrations.booking.secretKey}
                                onChange={(e) => updateIntegration('booking', 'secretKey', e.target.value)}
                                className="w-full text-xs font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-3 py-2 pr-9 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                                placeholder="Leave empty to keep current"
                              />
                              <button type="button" onClick={() => toggleSecret('bookingSecret')} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-neutral hover:text-brand-dark">
                                {showSecrets.bookingSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Category Status UIDs */}
                      <div className="space-y-3">
                        <h4 className="text-[11px] font-bold text-brand-dark uppercase tracking-wider flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                          Category Status UIDs
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(editIntegrations.zuper.categoryStatusUids).map(([catName, uids]) => (
                            <div key={catName} className="px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60 space-y-2">
                              <span className="text-xs font-medium text-brand-dark">{catName}</span>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-[10px] text-brand-neutral block">TBC</label>
                                  <input
                                    type="text"
                                    value={uids?.tbc || ''}
                                    onChange={(e) => updateCategoryUid(catName, 'tbc', e.target.value)}
                                    className="w-full text-[11px] font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                                    placeholder="UUID"
                                  />
                                </div>
                                <div>
                                  <label className="text-[10px] text-brand-neutral block">Scheduled</label>
                                  <input
                                    type="text"
                                    value={uids?.scheduled || ''}
                                    onChange={(e) => updateCategoryUid(catName, 'scheduled', e.target.value)}
                                    className="w-full text-[11px] font-mono text-brand-dark bg-white border border-brand-light/80 rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                                    placeholder="UUID"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Save/Cancel */}
                      <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                          onClick={cancelEditingIntegrations}
                          className="text-xs text-brand-neutral hover:text-brand-dark px-3 py-1.5 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={saveIntegrations}
                          disabled={updateMutation.isPending}
                          className="flex items-center gap-1.5 text-xs font-medium text-white bg-brand-primary hover:bg-brand-secondary px-4 py-2 rounded-xl transition-colors disabled:opacity-50 shadow-sm"
                        >
                          <Save className="w-3 h-3" />
                          Save
                        </button>
                      </div>
                    </>
                  ) : (
                    /* View Mode */
                    <div className="space-y-4">
                      {/* Zuper */}
                      <div className="space-y-1.5">
                        <h4 className="text-[11px] font-bold text-brand-dark uppercase tracking-wider flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                          Zuper API
                        </h4>
                        <div className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                          <span className="text-xs text-brand-neutral">Base URL</span>
                          <span className="text-xs font-mono text-brand-dark">{settings?.zuper?.apiBaseUrl || '—'}</span>
                        </div>
                        <div className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                          <span className="text-xs text-brand-neutral">API Token</span>
                          <span className="text-xs font-mono text-brand-dark">{settings?.zuper?.apiToken || '—'}</span>
                        </div>
                      </div>

                      {/* WhatsApp */}
                      <div className="space-y-1.5">
                        <h4 className="text-[11px] font-bold text-brand-dark uppercase tracking-wider flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                          WhatsApp / DoubleTick
                        </h4>
                        <div className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                          <span className="text-xs text-brand-neutral">API URL</span>
                          <span className="text-xs font-mono text-brand-dark">{settings?.whatsapp?.apiUrl || '—'}</span>
                        </div>
                        <div className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                          <span className="text-xs text-brand-neutral">API Key</span>
                          <span className="text-xs font-mono text-brand-dark">{settings?.whatsapp?.apiKey || '—'}</span>
                        </div>
                        <div className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                          <span className="text-xs text-brand-neutral">WABA Number</span>
                          <span className="text-xs font-mono text-brand-dark">{settings?.whatsapp?.wabaNumber || '—'}</span>
                        </div>
                        <div className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                          <span className="text-xs text-brand-neutral">Reminder Template</span>
                          <span className="text-xs font-mono text-brand-dark">{settings?.whatsapp?.templateName || '—'}</span>
                        </div>
                        <div className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                          <span className="text-xs text-brand-neutral">Confirmation Template</span>
                          <span className="text-xs font-mono text-brand-dark">{settings?.whatsapp?.confirmationTemplateName || '—'}</span>
                        </div>
                        {settings?.bookingBaseUrl && (
                          <div className="px-3 py-2.5 bg-amber-50 rounded-xl border border-amber-200">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-brand-neutral">Webhook URL <span className="text-amber-600">(configure in DoubleTick)</span></span>
                              <button
                                onClick={() => navigator.clipboard.writeText(`${settings.bookingBaseUrl}/api/webhooks/whatsapp`)}
                                className="text-[10px] text-brand-primary hover:underline"
                              >
                                Copy
                              </button>
                            </div>
                            <span className="text-xs font-mono text-brand-dark break-all">{settings.bookingBaseUrl}/api/webhooks/whatsapp</span>
                          </div>
                        )}
                      </div>

                      {/* Booking */}
                      <div className="space-y-1.5">
                        <h4 className="text-[11px] font-bold text-brand-dark uppercase tracking-wider flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                          Booking
                        </h4>
                        <div className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                          <span className="text-xs text-brand-neutral">Base URL</span>
                          <span className="text-xs font-mono text-brand-dark">{settings?.booking?.baseUrl || '—'}</span>
                        </div>
                        <div className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                          <span className="text-xs text-brand-neutral">Secret Key</span>
                          <span className="text-xs font-mono text-brand-dark">{settings?.booking?.secretKey || '—'}</span>
                        </div>
                      </div>

                      {/* System Info */}
                      <div className="space-y-1.5">
                        <h4 className="text-[11px] font-bold text-brand-dark uppercase tracking-wider flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                          System
                        </h4>
                        <div className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                          <span className="text-xs text-brand-neutral">Active Mechanics</span>
                          <span className="text-xs font-medium text-brand-dark">{mechanics.length}</span>
                        </div>
                        <div className="flex items-center justify-between px-3 py-2.5 bg-brand-light/20 rounded-xl border border-brand-light/60">
                          <span className="text-xs text-brand-neutral">Job Filters</span>
                          <span className="text-xs text-brand-neutral">TBC + AMC Workshop + Individual</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              </>)}
            </>
          )}
          {activeSection === 'job-categories' && (() => {
            const customCount = allCategories.filter(c => c.custom).length;
            const zuperCount = allCategories.length - customCount;
            return (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-center justify-between">
                  <p className="text-xs text-brand-neutral">
                    <strong>{allCategories.length}</strong> total — <strong>{zuperCount}</strong> from Zuper, <strong>{customCount}</strong> custom
                  </p>
                  <button
                    onClick={() => syncZuperMutation.mutate()}
                    disabled={syncZuperMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-dark bg-white border border-brand-light rounded-xl hover:bg-brand-light transition-colors shadow-sm disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${syncZuperMutation.isPending ? 'animate-spin' : ''}`} />
                    {syncZuperMutation.isPending ? 'Syncing...' : 'Sync Zuper'}
                  </button>
                </div>

                {/* Categories list */}
                <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                  <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white">
                    <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                      <Tag className="w-4 h-4 text-brand-primary" />
                      Job Categories
                    </h3>
                    <p className="text-xs text-brand-neutral mt-0.5">Manage Zuper and custom job categories</p>
                  </div>
                  <div className="p-4 space-y-1.5">
                    {allCategories.length === 0 ? (
                      <p className="text-sm text-brand-neutral text-center py-4">No categories found. Add one below.</p>
                    ) : allCategories.map(cat => {
                      const isExpanded = expandedCatUid === cat.category_uid;
                      const isEditing = editingCatUid === cat.category_uid;
                      return (
                      <div
                        key={cat.category_uid}
                        className={`rounded-xl border bg-white transition-all ${cat.custom ? 'border-brand-primary/20' : 'border-brand-light'}`}
                      >
                        <div className="flex items-center gap-3 px-4 py-3">
                        {isEditing ? (
                          <div className="flex-1 flex items-center gap-2">
                            <input
                              type="text"
                              value={editCatName}
                              onChange={e => setEditCatName(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && handleSaveEditCat()}
                              className="flex-1 px-3 py-1.5 border border-brand-light rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                              autoFocus
                            />
                            <button
                              onClick={handleSaveEditCat}
                              disabled={!editCatName.trim() || editCatMutation.isPending}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-40"
                            >
                              {editCatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => { setEditingCatUid(null); setEditCatName(''); }}
                              className="p-1.5 text-brand-neutral hover:bg-brand-light rounded-lg transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => setExpandedCatUid(isExpanded ? null : cat.category_uid)}
                              className="flex-1 min-w-0 flex items-center gap-2 text-left"
                              title="Show statuses for this category"
                            >
                              <ChevronRight className={`w-4 h-4 text-brand-neutral flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-brand-dark truncate">{cat.category_name}</p>
                                <p className="text-[10px] text-brand-neutral font-mono truncate">{cat.category_uid}</p>
                              </div>
                            </button>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${cat.custom ? 'bg-amber-50 text-amber-600 border border-amber-200' : 'bg-brand-light text-brand-neutral border border-brand-light'}`}>
                              {cat.custom ? 'Custom' : 'Zuper'}
                            </span>
                            {cat.custom && (
                              <div className="flex items-center gap-0.5 flex-shrink-0">
                                <button
                                  onClick={() => { setEditingCatUid(cat.category_uid); setEditCatName(cat.category_name); }}
                                  className="p-1.5 text-brand-neutral hover:text-brand-primary hover:bg-brand-light rounded-lg transition-colors"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => deleteCatMutation.mutate(cat.category_uid)}
                                  disabled={deleteCatMutation.isPending}
                                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </>
                        )}
                        </div>

                        {/* Expanded: this category's statuses (name + UID), discovered from Zuper */}
                        {isExpanded && !isEditing && (
                          <div className="border-t border-brand-light px-4 py-3 bg-[#f9fbfa]">
                            {catStatusesLoading ? (
                              <div className="flex items-center gap-2 text-xs text-brand-neutral">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading statuses…
                              </div>
                            ) : catStatusesError ? (
                              <p className="text-xs text-red-500">Failed to load statuses for this category.</p>
                            ) : catStatusData?.statuses?.length ? (
                              <div className="space-y-1">
                                <p className="text-[10px] text-brand-neutral mb-1.5">
                                  {catStatusData.statuses.length} statuses — discovered from recent Zuper jobs
                                </p>
                                {catStatusData.statuses.map(s => (
                                  <div key={s.status_uid} className="flex items-center gap-2 py-0.5">
                                    <span className="text-xs text-brand-dark">{s.name}</span>
                                    {s.isScheduled && (
                                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">Scheduled</span>
                                    )}
                                    {s.isTbc && (
                                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">TBC</span>
                                    )}
                                    <span className="ml-auto text-[10px] font-mono text-brand-neutral truncate">{s.status_uid}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-brand-neutral">No statuses found (no recent jobs in this category).</p>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>

                {/* Add new category */}
                <div className="bg-white rounded-2xl shadow-sm border border-brand-light p-4 space-y-3">
                  <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                    <Plus className="w-4 h-4 text-brand-primary" />
                    Add Custom Category
                  </h3>
                  <p className="text-xs text-brand-neutral">Copy the Category UID from the Zuper dashboard URL when viewing the category.</p>
                  <div className="space-y-2">
                    <div>
                      <label className="text-[10px] text-brand-neutral mb-1 block">Category Name</label>
                      <input
                        type="text"
                        value={newCatName}
                        onChange={e => setNewCatName(e.target.value)}
                        placeholder="e.g. AMC Collection & Valet"
                        className="w-full px-3 py-2 border border-brand-light rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-brand-neutral mb-1 block">Category UID (from Zuper)</label>
                      <input
                        type="text"
                        value={newCatUid}
                        onChange={e => setNewCatUid(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddCat()}
                        placeholder="e.g. 3fee25f0-74a1-11ea-8ca5-df1d176880cb"
                        className="w-full px-3 py-2 border border-brand-light rounded-xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary font-mono"
                      />
                    </div>
                    <button
                      onClick={handleAddCat}
                      disabled={!newCatName.trim() || !newCatUid.trim() || addCatMutation.isPending}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-brand-primary to-brand-secondary rounded-xl hover:shadow-lg transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {addCatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Add Category
                    </button>
                    {addCatMutation.isError && (
                      <p className="text-xs text-red-500">
                        {addCatMutation.error?.response?.data?.error || 'Failed to add category'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
          {activeSection === 'filters' && <FiltersSection />}
          {activeSection === 'database' && <DatabaseResetSection />}
          </div>
        </div>
      </main>
    </Layout>
  );
}

function TableResetRow({ t, confirmTable, setConfirmTable, handleReset, resetting }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-brand-light/60 hover:bg-brand-light/20 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-brand-dark">{t.label}</p>
          <span className="text-[10px] font-mono text-brand-neutral bg-brand-light px-1.5 py-0.5 rounded">
            {t.count} rows
          </span>
        </div>
        <p className="text-[11px] text-brand-neutral mt-0.5">{t.description}</p>
      </div>
      {confirmTable === t.key ? (
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => handleReset(t.key)}
            disabled={resetting === t.key}
            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
          >
            {resetting === t.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            Confirm
          </button>
          <button
            onClick={() => setConfirmTable(null)}
            className="px-3 py-1.5 text-[11px] font-medium text-brand-neutral bg-brand-light rounded-lg hover:bg-brand-light/80 transition-colors"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirmTable(t.key)}
          disabled={t.count === 0}
          className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
        >
          <Trash2 className="w-3 h-3" />
          Reset
        </button>
      )}
    </div>
  );
}

function FiltersSection() {
  const { selectedLocationId, currentLocation } = useLocationContext();
  const { data: filters, isLoading } = useQuery({
    queryKey: ['filter-settings', selectedLocationId],
    queryFn: async () => { const { data } = await axios.get('/api/admin/filters'); return data; },
  });
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setForm({ ...filters });
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setForm(null); };
  const save = async () => {
    setSaving(true);
    try {
      await axios.put('/api/admin/filters', form);
      queryClient.invalidateQueries({ queryKey: ['filter-settings'] });
      setEditing(false);
      setForm(null);
    } catch { /* ignore */ }
    setSaving(false);
  };

  const updateCategories = (val) => {
    setForm(f => ({ ...f, job_categories: val.split(',').map(s => s.trim()).filter(Boolean) }));
  };
  const updateStatuses = (key, val) => {
    setForm(f => ({ ...f, [key]: val.split(',').map(s => s.trim()).filter(Boolean) }));
  };

  if (isLoading) return null;

  const f = editing ? form : filters;
  if (!f) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
      <div className="p-5 border-b border-brand-light/60 flex justify-between items-center">
        <div>
          <h2 className="text-base font-bold text-brand-dark flex items-center gap-2">
            <SettingsIcon className="w-4 h-4 text-brand-primary" />
            Filters
          </h2>
          <p className="text-xs text-brand-neutral mt-0.5">Global filters applied across all pages</p>
        </div>
        {!editing ? (
          <button onClick={startEdit} className="text-xs font-medium text-brand-primary hover:underline">Edit</button>
        ) : (
          <div className="flex gap-2">
            <button onClick={cancel} className="px-3 py-1.5 text-xs font-medium text-brand-neutral border border-brand-light rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-3 py-1.5 text-xs font-medium text-white bg-brand-primary rounded-lg hover:bg-brand-secondary disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>
      <div className="p-5 space-y-4">
        <FilterRow label="Job Categories" hint="Comma-separated. Jobs outside these categories are hidden everywhere." editing={editing}
          value={f.job_categories?.join(', ')} onChange={v => updateCategories(v)} />
        <FilterRow label="Exclude Organization Customers" hint="Skip jobs linked to organizations (B2B)." editing={editing}
          type="toggle" value={f.exclude_org_customers} onChange={v => setForm(fm => ({ ...fm, exclude_org_customers: v }))} />
        <FilterRow label="TBC Search Range (weeks)" hint="How many weeks ahead to search in TBC Search." editing={editing}
          type="number" value={f.tbc_search_weeks} onChange={v => setForm(fm => ({ ...fm, tbc_search_weeks: parseInt(v) || 16 }))} />
        <FilterRow label="TBC Statuses" hint="Zuper status names considered as TBC." editing={editing}
          value={f.job_statuses_tbc?.join(', ')} onChange={v => updateStatuses('job_statuses_tbc', v)} />
        <FilterRow label="Scheduled Statuses" hint="Zuper status names considered as Scheduled." editing={editing}
          value={f.job_statuses_scheduled?.join(', ')} onChange={v => updateStatuses('job_statuses_scheduled', v)} />
        <FilterRow label="Completed Statuses" hint="Zuper status names considered as Completed (past Scheduled)." editing={editing}
          value={f.job_statuses_completed?.join(', ')} onChange={v => updateStatuses('job_statuses_completed', v)} />
        <FilterRow label="Reminder Week Cycle" hint="Campaign/reminder week cycle." editing={editing}
          value={f.reminder_week_cycle} onChange={v => setForm(fm => ({ ...fm, reminder_week_cycle: v }))} />
        <FilterRow label="Dashboard Week Cycle" hint="Dashboard display week cycle." editing={editing}
          value={f.dashboard_week_cycle} onChange={v => setForm(fm => ({ ...fm, dashboard_week_cycle: v }))} />
        {currentLocation && currentLocation.name !== 'All Locations' ? (
          <FilterRow label={`Job Prefix (${currentLocation.name})`} hint="Only show jobs whose work order starts with this prefix. Comma-separated for multiple (e.g. AMC,AMCT). Empty = show all jobs at this location." editing={editing}
            value={editing ? (form.location_job_prefix ?? f.job_prefix ?? '') : (f.job_prefix ?? '')}
            onChange={v => setForm(fm => ({ ...fm, location_job_prefix: v }))} />
        ) : (
          <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <strong>Job Prefix is per-location.</strong> Switch to a specific location above to set its prefix.
          </div>
        )}
      </div>
    </div>
  );
}

function FilterRow({ label, hint, editing, value, onChange, type = 'text' }) {
  if (type === 'toggle') {
    return (
      <div className="flex items-center justify-between py-2 border-b border-brand-light/40 last:border-0">
        <div>
          <p className="text-sm font-medium text-brand-dark">{label}</p>
          <p className="text-[11px] text-brand-neutral">{hint}</p>
        </div>
        {editing ? (
          <button onClick={() => onChange(!value)}
            className={`w-10 h-5 rounded-full transition-colors relative ${value ? 'bg-brand-primary' : 'bg-gray-300'}`}>
            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? 'left-5' : 'left-0.5'}`} />
          </button>
        ) : (
          <span className={`text-xs font-medium px-2 py-0.5 rounded ${value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {value ? 'Yes' : 'No'}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between py-2 border-b border-brand-light/40 last:border-0 gap-4">
      <div className="flex-shrink-0">
        <p className="text-sm font-medium text-brand-dark">{label}</p>
        <p className="text-[11px] text-brand-neutral">{hint}</p>
      </div>
      {editing ? (
        <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
          className="text-sm text-right border border-brand-light rounded-lg px-3 py-1.5 w-64 focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
      ) : (
        <span className="text-sm text-brand-neutral font-mono">{String(value || '—')}</span>
      )}
    </div>
  );
}

function DatabaseResetSection() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(null);
  const [confirmTable, setConfirmTable] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/settings/db-stats');
      setStats(data.stats);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchStats(); }, []);

  const handleReset = async (tableKey) => {
    setResetting(tableKey);
    try {
      const { data } = await axios.post('/api/settings/db-reset', { table: tableKey });
      const total = Object.values(data.deleted).reduce((a, b) => a + b, 0);
      setToast(`Cleared ${total} record${total !== 1 ? 's' : ''} from ${tableKey}`);
      setTimeout(() => setToast(null), 3000);
      fetchStats();
    } catch (err) {
      setToast(`Failed: ${err.response?.data?.error || err.message}`);
      setTimeout(() => setToast(null), 3000);
    }
    setResetting(null);
    setConfirmTable(null);
  };

  const dataTables = (stats || []).filter(t => t.category === 'data' || !t.category);
  const configTables = (stats || []).filter(t => t.category === 'config');

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-red-200/60 overflow-hidden">
      <div className="px-4 md:px-6 py-3 md:py-4 border-b border-red-100 bg-red-50/30">
        <h3 className="text-sm font-bold text-red-700 flex items-center gap-2">
          <Trash2 className="w-4 h-4" />
          Database Reset
        </h3>
        <p className="text-xs text-red-500/80 mt-0.5">
          Clear table data to fix duplicates or stale records. Sync after reset to re-import.
        </p>
      </div>
      <div className="p-4 md:p-6">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
          </div>
        ) : (
          <div className="space-y-2">
            {dataTables.map((t) => (
              <TableResetRow key={t.key} t={t} confirmTable={confirmTable} setConfirmTable={setConfirmTable} handleReset={handleReset} resetting={resetting} />
            ))}

            {configTables.length > 0 && (
              <>
                <div className="pt-3 pb-1">
                  <p className="text-[10px] font-semibold text-red-400 uppercase tracking-wider px-1">Configuration (use with caution)</p>
                </div>
                {configTables.map((t) => (
                  <TableResetRow key={t.key} t={t} confirmTable={confirmTable} setConfirmTable={setConfirmTable} handleReset={handleReset} resetting={resetting} />
                ))}
              </>
            )}

            {confirmTable && (
              <div className="mt-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
                This will permanently delete all data in this table.
              </div>
            )}
          </div>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-brand-dark text-white px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}
